// src/admin/lib/backgroundJobs.js
//
// Ticket 82. The rules behind Health's Background section, kept out of the
// component so they can be run and tested on their own (scripts/check-background-jobs.mjs).
//
// WHAT THE DATABASE SENDS: one row per job in cron.job (admin_background_jobs):
// name, schedule, whether it is switched on, the expected gap between runs in
// seconds (null when the schedule isn't one it can read), the status of its newest
// run, how many seconds ago that run started, and the error if it failed. And five
// numbers about reflections (admin_reflection_states). Nothing else.
//
// THE SCREEN READS cron.job, NOT THIS FILE. EXPECTED_JOBS is only a list of the
// jobs the migrations schedule, used for one thing: a job that is missing from
// cron.job (unscheduled, or never scheduled on this database) can't appear as a
// row, so without a list it would be invisible, which is exactly the failure this
// screen exists to catch. A job that is in cron.job and not in this list still
// shows, as an ordinary row. scripts/check-background-jobs.mjs keeps this list in
// step with the migrations.
//
// WHAT "SUCCEEDED" MEANS. cron.job_run_details says Postgres ran the job's SQL.
// Four of the six jobs only send a web request to an edge function, and are
// finished the moment it is sent, whether or not the function worked. `how`
// records which kind each job is, so the row can say so. For a job this list has
// never heard of, the row says the weaker thing that is true of either kind.
//
// UNKNOWN IS NOT FINE. A schedule that can't be read, a job with no run on
// record, and a number that failed to load are each shown as unknown, never as
// "on time" or 0.

export const OVERDUE_FACTOR = 2;               // flagged when it hasn't run in this many intervals
export const WAITING_TOO_LONG_SECONDS = 30 * 60; // 6 ticks of embed-reflections (every 5 minutes)

const IN_PROGRESS = new Set(['starting', 'running', 'sending', 'connecting']);

// how: 'edge' = the job only sends a request to an edge function; 'sql' = the work
// happens inside the database.
export const EXPECTED_JOBS = [
  { name: 'geocode-events',        how: 'edge', stops: 'Event addresses stop being turned into map locations.' },
  { name: 'expire-thoughts',       how: 'sql',  stops: 'Expired thoughts stop being cleared out.' },
  { name: 'cache-book-covers',     how: 'edge', stops: 'Book covers stop being copied to our own storage.' },
  { name: 'voice-retention-sweep', how: 'edge', stops: 'Old voice notes are no longer checked against the retention rule.',
    note: 'Still a dry run: it reports what it would delete and deletes nothing.' },
  { name: 'embed-reflections',     how: 'edge', stops: 'Journal entries stop becoming searchable for matching.',
    note: 'Watch the waiting entries below. If runs look fine and entries keep waiting, the function is failing, most often on a missing or rejected key.' },
  { name: 'refresh-community-stats', how: 'sql', stops: 'The Community Stats numbers stop being added up, so the tab keeps showing an older date.' },
];

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// 45 -> "45 seconds", 300 -> "5 minutes", 7200 -> "2 hours", 172800 -> "2 days"
export function humanDuration(seconds) {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return null;
  const s = Math.max(0, Math.round(Number(seconds)));
  if (s < 60) return plural(s, 'second');
  const m = Math.round(s / 60);
  if (m < 60) return plural(m, 'minute');
  const h = Math.round(s / 3600);
  if (h < 48) return plural(h, 'hour');
  return plural(Math.round(s / 86400), 'day');
}

// "every 5 minutes", "every day"; the schedule text itself when it couldn't be read.
export function everyText(interval, schedule) {
  if (interval === null || interval === undefined) return `on the schedule "${schedule}" (how often is not worked out)`;
  if (interval === 60) return 'every minute';
  if (interval === 3600) return 'every hour';
  if (interval === 86400) return 'every day';
  if (interval === 604800) return 'every week';
  return `every ${humanDuration(interval)}`;
}

// What a successful run tells you, in words.
export function meaningOfSuccess(job) {
  const known = EXPECTED_JOBS.find(e => e.name === job.job_name);
  if (!known) return 'Succeeded means the database ran the job. If it calls a function, that only says the request went out.';
  if (known.how === 'sql') return 'Succeeded means the work ran inside the database.';
  return 'Succeeded only means the request was sent. It does not say the function then did its work.';
}

// One job row in, one verdict out: { tone: 'ok' | 'warn' | 'bad' | 'neutral', label, detail? }
export function jobState(job) {
  const interval = job.interval_seconds ?? null;
  const ago = job.last_started_ago_seconds ?? null;
  const status = job.last_status ?? null;

  if (job.active === false) {
    return { tone: 'warn', label: 'Switched off', detail: 'This job is in the schedule but turned off, so it will not run.' };
  }
  if (status === null) {
    return { tone: 'warn', label: 'No run on record', detail: 'It has never run, or its run history was cleared. Unknown, not fine.' };
  }
  if (status === 'failed') {
    return { tone: 'bad', label: 'Last run failed', detail: ago === null ? undefined : `Started ${humanDuration(ago)} ago.` };
  }
  if (IN_PROGRESS.has(status)) {
    if (interval !== null && ago !== null && ago > OVERDUE_FACTOR * interval) {
      return { tone: 'bad', label: 'Not finishing', detail: `Started ${humanDuration(ago)} ago and still has not finished. It should take far less than ${humanDuration(interval)}.` };
    }
    return { tone: 'neutral', label: 'Running now', detail: ago === null ? undefined : `Started ${humanDuration(ago)} ago.` };
  }
  if (status === 'succeeded') {
    if (interval === null) {
      return { tone: 'warn', label: `Ran ${humanDuration(ago)} ago`, detail: 'This schedule is not one the screen can read, so it cannot say whether the job is overdue.' };
    }
    if (ago !== null && ago > OVERDUE_FACTOR * interval) {
      return { tone: 'bad', label: 'Overdue', detail: `Last ran ${humanDuration(ago)} ago. It should run ${everyText(interval)}.` };
    }
    return { tone: 'ok', label: `Ran ${humanDuration(ago)} ago` };
  }
  return { tone: 'warn', label: `Last run: ${status}`, detail: 'A status the screen has no rule for.' };
}

// Expected jobs that aren't in cron.job. These can't show as rows.
export function missingJobs(rows) {
  const present = new Set((rows || []).map(r => r.job_name));
  return EXPECTED_JOBS.filter(e => !present.has(e.name));
}

// How many things on the jobs list need a person: bad rows, switched-off or
// unreadable ones, and missing jobs. Used for the headline above the list.
export function jobsNeedingAttention(rows) {
  const flagged = (rows || []).filter(r => jobState(r).tone !== 'ok' && jobState(r).tone !== 'neutral').length;
  return flagged + missingJobs(rows).length;
}

// The reflection counts. A count that didn't load is null and stays null.
export function reflectionVerdicts(c) {
  const out = { failed: { tone: 'neutral' }, waiting: { tone: 'neutral' } };
  if (!c) return out;
  if (c.failed === null || c.failed === undefined) out.failed = { tone: 'warn', text: 'Could not be counted.' };
  else if (c.failed > 0) out.failed = { tone: 'bad', text: 'The embedder rejected these entries. The schedule never retries a failed entry.' };
  else out.failed = { tone: 'ok', text: 'None.' };

  if (c.waiting === null || c.waiting === undefined) {
    out.waiting = { tone: 'warn', text: 'Could not be counted.' };
  } else if (c.waiting === 0) {
    out.waiting = { tone: 'ok', text: 'Nothing is waiting.' };
  } else if (c.oldest_waiting_seconds === null || c.oldest_waiting_seconds === undefined) {
    out.waiting = { tone: 'warn', text: 'How long the oldest has waited is unknown.' };
  } else if (c.oldest_waiting_seconds > WAITING_TOO_LONG_SECONDS) {
    out.waiting = { tone: 'bad', text: `The oldest has waited ${humanDuration(c.oldest_waiting_seconds)}. Entries normally wait only a few minutes.` };
  } else {
    out.waiting = { tone: 'ok', text: `The oldest has waited ${humanDuration(c.oldest_waiting_seconds)}.` };
  }
  return out;
}

// The one row the function returns for reflections, or null if it came back empty
// or malformed (shown as unknown, never as zeros).
export function firstReflectionRow(data) {
  const row = Array.isArray(data) ? data[0] : data;
  if (!row || typeof row !== 'object') return null;
  const n = v => (v === null || v === undefined ? null : Number(v));
  return {
    waiting: n(row.waiting),
    nothing_to_embed: n(row.nothing_to_embed),
    embedded: n(row.embedded),
    failed: n(row.failed),
    oldest_waiting_seconds: n(row.oldest_waiting_seconds),
  };
}

// bigint columns can arrive as strings; make them numbers, keep nulls null.
export function jobRows(data) {
  if (!Array.isArray(data)) return null;
  const n = v => (v === null || v === undefined ? null : Number(v));
  return data.map(r => ({
    job_id: n(r.job_id),
    job_name: r.job_name,
    schedule: r.schedule,
    active: r.active,
    interval_seconds: n(r.interval_seconds),
    last_status: r.last_status ?? null,
    last_started_ago_seconds: n(r.last_started_ago_seconds),
    last_error: r.last_error ?? null,
  }));
}
